import { styled } from '@mui/material/styles';
import { zIndexLayers } from '../../../theme/zIndexLayers';
import { GALLERY_TILE_GAP } from '../utils/galleryLayout';

/**
 * SC-375: the gallery covers the game canvas only. It sits above the map and its HUD but below chat,
 * menus and the bottom toolbar, so the call controls (and the layout switch back) stay reachable.
 */
export const GALLERY_Z_INDEX = zIndexLayers.chat - 50;

/** Clears the bottom toolbar, matching the gap the sidebar layouts leave. */
const BOTTOM_CHROME_CLEARANCE = 100;

export const GalleryRoot = styled('div')({
  position: 'fixed',
  inset: 0,
  zIndex: GALLERY_Z_INDEX,
  display: 'flex',
  flexDirection: 'column',
  padding: `12px 16px ${BOTTOM_CHROME_CLEARANCE}px`,
  boxSizing: 'border-box',
  backgroundColor: 'var(--sevv-bg-default, #121212)',
});

export const GalleryHeader = styled('div')({
  display: 'flex',
  justifyContent: 'flex-end',
  alignItems: 'center',
  minHeight: 40,
  marginBottom: 8,
  flexShrink: 0,
});

/** Measured for the grid geometry; tiles are centred in it, so an odd last row sits in the middle. */
export const GalleryGridArea = styled('div')({
  flex: 1,
  minHeight: 0,
  display: 'flex',
  flexWrap: 'wrap',
  alignContent: 'center',
  justifyContent: 'center',
  gap: `${GALLERY_TILE_GAP}px`,
  overflow: 'hidden',
});

export const GalleryCell = styled('div')({
  position: 'relative',
  flexShrink: 0,
  flexGrow: 0,
});

/**
 * Tile frame inside a gallery cell. Unlike the sidebar tile it never scales up when speaking: the grid
 * is packed edge to edge, so a scaled tile would cover its neighbours. The ring alone shows who talks.
 */
export const GalleryVideoWrapper = styled('div', {
  shouldForwardProp: (prop) => prop !== 'panelPosition',
})<{ panelPosition?: unknown }>({
  position: 'absolute',
  inset: 0,
  borderRadius: 12,
  overflow: 'hidden',
  backgroundColor: 'var(--sevv-gray-800, #1f1f1f)',
  border: '2px solid var(--sevv-border-subtle, rgba(255, 255, 255, 0.2))',
  boxSizing: 'border-box',
  transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
  '& video': {
    objectFit: 'cover',
    width: '100%',
    height: '100%',
  },
  '&.speaking': {
    borderColor: 'var(--sevv-success, #4caf50)',
    boxShadow: '0 0 0 2px var(--sevv-success, #4caf50)',
  },
  '&.screen-sharing': {
    borderColor: 'var(--sevv-info, #2196f3)',
  },
});

export const GalleryOverflowTile = styled('div')({
  position: 'absolute',
  inset: 0,
  borderRadius: 12,
  overflow: 'hidden',
  boxSizing: 'border-box',
  border: '2px solid var(--sevv-border-subtle, rgba(255, 255, 255, 0.2))',
  backgroundColor: 'var(--sevv-gray-800, #1f1f1f)',
  color: 'var(--sevv-white, #ffffff)',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 12,
});

export const GalleryOverflowPictures = styled('div')({
  display: 'flex',
  justifyContent: 'center',
  '& > *:not(:first-of-type)': {
    marginLeft: -12,
  },
});

export const GalleryOverflowLabel = styled('div')({
  fontSize: 16,
  fontWeight: 600,
});
